import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { conditionalRender, valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { SendIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { toast } from 'sonner'

import { getForeignStaffTravelSummary } from '../../../api/affected-staffs/get-foreign-staff-travel-summary'
import { sendArrivalChoiceEmail } from '../../../api/affected-staffs/send-arrival-choice-email'
import { getTrips } from '../../../api/trips/get-trips'
import {
  getTravelChoiceEnumMapping,
  TravelChoiceEnum
} from '../../../lib/types/enums/affected-staff/travel-choice.enum'
import { AffectedStaff } from '../../../lib/types/models/affectedStaff/affected-staff'
import { AffectedStaffInput } from '../../../lib/types/models/affectedStaff/affected-staff-input'
import { AffectedStaffProperties } from '../../../lib/types/models/affectedStaff/properties.affected-staff'
import { StaffRequestProperties } from '../../../lib/types/models/staff-request/properties.staff-request'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { TripBookingDispatchPlaceLight } from '../../../lib/types/models/trip-booking-dispatch-places/trip-booking-dispatch-place'
import { TripBookingDispatchPlaceProperties } from '../../../lib/types/models/trip-booking-dispatch-places/trip-booking-dispatch-place.properties'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import { ControlledListRadioGroup } from '../../elements/inputs/enum-radio-group'
import LabelWithText from '../ols/label-with-text'
import ForeignStaffTravelSummary from './foreign-staff-travel-summary'

type TripScheduleInfoProps = {
  trip?: TripBookingDispatchPlaceLight
  label: string
  variant: 'warning' | 'success'
}

const TripScheduleInfo = ({ trip, label, variant }: TripScheduleInfoProps) => {
  const formatTime = (time?: { hours: number; minutes: number }) =>
    time ? (DateFormatter.formatTimeString(time.hours, time.minutes) ?? '') : ''

  // getAppointment expects the light/partial variant — cast here since dispatch places
  // for affected staff come as the light representation
  const appointment = trip ? TripBookingDispatchPlaceProperties.getAppointment(trip) : null

  return (
    <div>
      <Badge text={label} variant={variant} />
      <div className="h-2" />
      <div className="grid grid-cols-3 items-center">
        <LabelWithText
          label="Horaires de voyage"
          data={
            <div className="flex gap-2">
              <Badge text={valueRender(trip?.tripStep?.departureTime, formatTime)} />
              <Badge text={valueRender(trip?.tripStep?.arrivalTime, formatTime)} />
            </div>
          }
        />
        <LabelWithText
          label="Heure de RDV"
          data={
            <Badge
              text={
                trip
                  ? valueRender(appointment?.time, (date) => DateFormatter.getFormattedTime(date))
                  : '-'
              }
            />
          }
        />
        <LabelWithText
          label="Lieu de RDV"
          data={trip ? valueRender(appointment?.place?.label) : '-'}
        />
      </div>
    </div>
  )
}

// ============================================================================
// Main Component
// ============================================================================

type Props = {
  affectedStaff: AffectedStaff
}

const AffectedStaffTripInformation = ({ affectedStaff }: Props) => {
  const form = useFormContext<AffectedStaffInput>()
  const staffRequest = affectedStaff.staffRequest
  const countryCode = staffRequest?.country?.code

  const { data: trips } = useQuery({
    queryKey: ['trips', countryCode],
    queryFn: () =>
      getTrips({
        filter: {
          ...(countryCode && { countryCodes: [countryCode] }),
          centerIds: staffRequest?.centers?.map((center) => center.id)
        }
      })
  })

  const sendEmailMutation = useMutation({
    mutationFn: sendArrivalChoiceEmail,
    onSuccess: () => toast.success('Email envoyé avec succès'),
    onError: showAxiosError
  })

  const selectedTripId = form.watch('tripId')
  const isTravelChoiceDirty = Boolean(form.formState.dirtyFields?.travelChoice)
  const isTripIdDirty = Boolean(form.formState.dirtyFields?.tripId)

  const isSameTrip = selectedTripId === affectedStaff.tripId
  const isStaffForeign = StaffRequestProperties.isForeignStaff(staffRequest)
  const shouldHideComponent = !staffRequest?.centers?.length

  // English (foreign) staff pick a travel choice instead of a trip (joining the group: Eurostar only)
  const canToggleTravelChoice = isStaffForeign

  const tripBookingDetails = useMemo(() => {
    if (!isSameTrip) return { allerTrip: undefined, retourTrip: undefined }
    return AffectedStaffProperties.getTripBookingDispatchPlaces(affectedStaff)
  }, [isSameTrip, affectedStaff])

  const tripOptions = trips?.data.map((trip) => ({
    label: TripProperties.getTripLabel(trip),
    value: trip.id
  }))
  const hasTravelChoice = Boolean(affectedStaff.travelChoice)

  // Same backend summary as the staff personal space, so both screens show the same GROUP label.
  // Same query key as ForeignStaffTravelSummary below: React Query dedupes it into one request.
  const { data: travelSummary } = useQuery({
    queryKey: [
      'affected-staff',
      affectedStaff.id,
      'foreign-travel-summary',
      affectedStaff.travelChoice
    ],
    queryFn: () => getForeignStaffTravelSummary(affectedStaff.id),
    enabled: isStaffForeign
  })
  const meetingPlace = travelSummary?.group.meetingPlaceLabel ?? undefined
  // Joining the group is only offered when the center has a Eurostar journey (backend rule);
  // a GROUP choice already saved stays listed so the admin still sees it
  const travelChoices =
    travelSummary?.group.canJoinGroup === false &&
    affectedStaff.travelChoice !== TravelChoiceEnum.GROUP
      ? [TravelChoiceEnum.INDIVIDUAL]
      : Object.values(TravelChoiceEnum)

  // English (foreign) staff cannot be assigned a trip: never send a stale trip id
  useEffect(() => {
    if (isStaffForeign && selectedTripId != null) {
      form.setValue('tripId', null, { shouldValidate: true })
    }
  }, [form, isStaffForeign, selectedTripId])

  const handleTripChange = (value: number | null | undefined) => {
    // ControlledSelectInput provides the selected option value (trip id), null for "Aucune"
    form.setValue('tripId', value, { shouldValidate: true })
    if (!canToggleTravelChoice) {
      form.setValue('travelChoice', null, { shouldValidate: true })
    }
  }

  const handleSendEmail = () => {
    if (isTravelChoiceDirty || isTripIdDirty) {
      toast.info("Veuillez enregistrer le 'Choix d'arrivée' avant d'envoyer l'email.")
      return
    }
    sendEmailMutation.mutate(affectedStaff.id)
  }

  return (
    <InnerBox className={cn('rounded-lg', { hidden: shouldHideComponent })}>
      {/* Header */}
      <div className="flex justify-between items-center">
        <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
          Informations sur le voyage
        </Typography>
        {canToggleTravelChoice && !hasTravelChoice && (
          <Button
            type="button"
            icon={SendIcon}
            disabled={sendEmailMutation.isPending}
            className="w-fit"
            variant="outline"
            label="Envoyer le choix d'arrivée"
            loading={sendEmailMutation.isPending}
            onClick={handleSendEmail}
          />
        )}
      </div>

      {/* Form Content */}
      <div className="flex flex-col justify-between h-full py-5">
        {/* Trip Selection — hidden for English (foreign) staff, who cannot have a trip */}
        {!isStaffForeign && (
          <FormField
            control={form.control}
            name="tripId"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    optional
                    label="Assigner le voyage"
                    options={tripOptions}
                    selectedOption={conditionalRender(!!field?.value, {
                      label: TripProperties.getTripLabel(
                        trips?.data.find((trip) => trip.id === field.value)
                      ),
                      value: field.value ?? undefined
                    })}
                    onChange={handleTripChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )}

        <div className="h-3" />

        {/* Travel Choice Radio Group */}
        {isStaffForeign && (
          <ControlledListRadioGroup<AffectedStaffInput, TravelChoiceEnum>
            required
            name="travelChoice"
            hideLabel
            list={travelChoices}
            mapping={getTravelChoiceEnumMapping(meetingPlace)}
          />
        )}

        {/* Same details the English staff sees in their personal space, from the saved choice */}
        {isStaffForeign && (
          <>
            <div className="h-3" />
            <ForeignStaffTravelSummary affectedStaff={affectedStaff} />
          </>
        )}

        {/* Trip schedule blocks only make sense when a trip can be assigned */}
        {!isStaffForeign && (
          <>
            <div className="h-3" />

            {/* Outbound Trip Info */}
            <TripScheduleInfo
              trip={tripBookingDetails.allerTrip}
              label="Aller"
              variant="warning"
            />

            <div className="h-3" />

            {/* Return Trip Info */}
            <TripScheduleInfo
              trip={tripBookingDetails.retourTrip}
              label="Retour"
              variant="success"
            />
          </>
        )}
      </div>
    </InnerBox>
  )
}

export default AffectedStaffTripInformation
